import {AxiosInstance} from 'axios';
import {authFetch} from '../auth/session';
import type {ParameterResolution, ScanCoverageGaps} from './types';

// SB-38 — who made a review. The UI can say which SURFACE acted, never which person: since
// SB-27 the server records a signed-in reviewer's user id itself and ignores this, so `ui` is
// stored only while auth is off, and the value reads as a surface on purpose. The automation
// counters class it as `human` by equality (`UI_STAMPS` in property_analysis/automation.py),
// and scripts/vocabulary_parity.py pins this union to that tuple: a renamed stamp would
// otherwise move every UI review into «other» without a word.
export type ReviewerSurface = 'ui';
const REVIEWED_BY_UI: ReviewerSurface = 'ui';

export function createParametersApi(client: AxiosInstance) {
    return {
        async getParameterConfig(): Promise<{value_types: string[]; provision_types: string[]}> {
            const response = await client.get('/api/parameters/config');
            return response.data;
        },
        async listParameters(): Promise<Array<{
            id: number; code: string; name: string; display_name: string | null;
            value_type: string | null; synonyms: string[]; section_count: number;
        }>> {
            const response = await client.get('/api/parameters');
            return response.data;
        },
        async getParameter(parameterId: number): Promise<{
            id: number; code: string; name: string; display_name: string | null;
            description: string | null; value_type: string | null;
            valid_range: Record<string, any> | null; synonyms: string[];
            sections: Array<{
                id: number; section_id: string; provision_type: string | null;
                notes: string | null; evidence_text: string | null; llm_reasoning: string | null;
                section_number: string | null; section_heading: string | null; doc_id: string | null;
            }>;
        }> {
            const response = await client.get(`/api/parameters/${parameterId}`);
            return response.data;
        },
        async createParameter(data: {
            code: string; name: string; display_name?: string; description?: string;
            value_type?: string; valid_range?: Record<string, any>; synonyms?: string[];
        }) {
            const response = await client.post('/api/parameters', data);
            return response.data;
        },
        async updateParameter(parameterId: number, data: {
            name?: string; display_name?: string; description?: string;
            value_type?: string; valid_range?: Record<string, any>; synonyms?: string[];
        }) {
            const response = await client.put(`/api/parameters/${parameterId}`, data);
            return response.data;
        },
        async deleteParameter(parameterId: number) {
            const response = await client.delete(`/api/parameters/${parameterId}`);
            return response.data;
        },
        async addParameterSection(parameterId: number, data: {section_id: string; provision_type?: string; notes?: string}) {
            const response = await client.post(`/api/parameters/${parameterId}/sections`, data);
            return response.data;
        },
        async removeParameterSection(parameterId: number, sectionId: string) {
            const response = await client.delete(`/api/parameters/${parameterId}/sections/${encodeURIComponent(sectionId)}`);
            return response.data;
        },
        async updateParameterSection(parameterId: number, linkId: number, data: {
            provision_type?: string | null; evidence_text?: string | null; llm_reasoning?: string | null;
        }) {
            const response = await client.put(`/api/parameters/${parameterId}/sections/${linkId}`, data);
            return response.data;
        },
        async suggestParameterSynonyms(params: {
            code: string; name: string; display_name?: string; description?: string; existing_synonyms?: string[];
        }): Promise<{synonyms: string[]; existing: string[]}> {
            const response = await client.post('/api/parameters/suggest-synonyms', params);
            return response.data;
        },

        // Binding Suggestions
        async getBindingSuggestionStats(): Promise<{
            total: number; pending: number; confirmed: number; rejected: number;
            by_parameter: Record<string, number>;
        }> {
            const response = await client.get('/api/parameters/suggestions/stats');
            return response.data;
        },
        async listParameterSuggestions(parameterId: number, status?: string): Promise<Array<{
            id: number; parameter_id: number; section_id: string; bm25_score: number | null;
            suggested_provision_type: string | null; llm_reasoning: string | null;
            evidence_text: string | null;
            provision_values: Array<Record<string, any>> | null;
            status: string; reviewed_by: string | null;
            reviewed_at: string | null; review_notes: string | null; created_at: string | null;
            section_number: string | null; section_heading: string | null; doc_id: string | null;
        }>> {
            const params: any = {};
            if (status) params.status = status;
            const response = await client.get(`/api/parameters/${parameterId}/suggestions`, {params});
            return response.data;
        },
        // The vocabulary a variant's `condition` may hold, SERVED rather than mirrored here
        // (SB-186). These tokens are matched by equality on the questions side, so a stale TS
        // copy would not raise — it would offer a value that matches nothing and the verdict
        // would read "no rule found". Fetch it; never hardcode it.
        async getConditionVocabulary(): Promise<{
            keys: Record<string, string[] | null>;
            consumer_answerable: string[];
        }> {
            const response = await client.get('/api/parameters/condition-vocabulary');
            return response.data;
        },
        // `correction` is SB-186's third option: omit it and the suggestion's values are stored
        // verbatim, exactly as before. Supplying provision_values stores those instead — scope
        // changes and added limits only; a moved number or a dropped limit comes back 422 with
        // {detail: {refused, reason}}.
        async confirmBindingSuggestion(
            parameterId: number,
            suggestionId: number,
            reviewedBy?: string,
            correction?: {provision_values: Array<Record<string, any>>; reason?: string},
        ): Promise<any> {
            const params: any = {reviewed_by: reviewedBy || REVIEWED_BY_UI};
            const response = await client.post(
                `/api/parameters/${parameterId}/suggestions/${suggestionId}/confirm`,
                correction ?? null,
                {params},
            );
            return response.data;
        },
        async rejectBindingSuggestion(parameterId: number, suggestionId: number, reviewNotes?: string, reviewedBy?: string): Promise<any> {
            const params: any = {reviewed_by: reviewedBy || REVIEWED_BY_UI};
            const body = reviewNotes ? {review_notes: reviewNotes} : undefined;
            const response = await client.post(`/api/parameters/${parameterId}/suggestions/${suggestionId}/reject`, body, {params});
            return response.data;
        },

        // Discovery
        async discoverParameterBindings(docId: string, options?: {
            storeResults?: boolean; clearExisting?: boolean; topKPerParam?: number; parameterIds?: number[];
        }): Promise<{
            doc_id: string; sections_scanned: number; candidates_found: number;
            llm_calls_made: number; bindings_discovered: number;
            bindings: Array<{
                parameter_id: number; parameter_code: string; section_id: string;
                bm25_score: number; provision_type: string; evidence_text: string;
                reasoning: string | null; value: number | string | null;
                unit: string | null; operator: string | null;
            }>;
            errors: string[]; tokens_in: number; tokens_out: number;
            bm25_candidates: Array<{section_id: string; doc_id: string; heading: string | null; bm25_score: number}> | null;
            rejections: Array<{parameter_code: string; section_id: string; evidence_text: string; reasoning: string}> | null;
        }> {
            const body: any = {
                doc_id: docId,
                store_results: options?.storeResults ?? true,
                clear_existing: options?.clearExisting ?? true,
                top_k_per_param: options?.topKPerParam ?? 10,
            };
            if (options?.parameterIds) body.parameter_ids = options.parameterIds;
            const response = await client.post('/api/parameters-discovery/discover', body);
            return response.data;
        },

        discoverParameterBindingsStream(docId: string, options?: {
            storeResults?: boolean; clearExisting?: boolean; topKPerParam?: number;
            parameterIds?: number[]; skipScanned?: boolean;
        }, callbacks?: {
            onProgress?: (progress: any) => void;
            onResult?: (result: any) => void;
            onError?: (error: string) => void;
        }): {abort: () => void} {
            const body = {
                doc_id: docId,
                store_results: options?.storeResults ?? true,
                clear_existing: options?.clearExisting ?? true,
                top_k_per_param: options?.topKPerParam ?? 10,
                parameter_ids: options?.parameterIds ?? null,
                skip_scanned: options?.skipScanned ?? true,
            };
            const abortController = new AbortController();
            authFetch('/api/parameters-discovery/discover-stream', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(body),
                signal: abortController.signal,
            })
                .then(response => {
                    if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                    if (!response.body) throw new Error('No response body');
                    const reader = response.body.getReader();
                    const decoder = new TextDecoder();
                    let buffer = '';
                    const processLine = (line: string) => {
                        if (line.startsWith('data: ')) {
                            try {
                                const data = JSON.parse(line.slice(6));
                                if (data.type === 'progress') callbacks?.onProgress?.(data);
                                else if (data.type === 'result') callbacks?.onResult?.(data);
                                else if (data.error) callbacks?.onError?.(data.error);
                            } catch { /* ignore */ }
                        }
                    };
                    const pump = (): Promise<void> => {
                        return reader.read().then(({done, value}) => {
                            if (done) return;
                            buffer += decoder.decode(value, {stream: true});
                            const lines = buffer.split('\n');
                            buffer = lines.pop() || '';
                            for (const line of lines) { if (line.trim()) processLine(line); }
                            return pump();
                        });
                    };
                    return pump();
                })
                .catch(error => {
                    if (error.name !== 'AbortError') callbacks?.onError?.(error.message || 'Discovery stream failed');
                });
            return {abort: () => abortController.abort()};
        },

        // Scan Coverage
        async getParameterScanCoverage(parameterId: number): Promise<Record<string, {last_scanned_at: string | null, bindings_found: number}>> {
            const response = await client.get(`/api/parameters-discovery/scan-coverage/${parameterId}`);
            return response.data;
        },
        // SB-37: the register the Parameters page shows, so a new parameter's
        // coverage hole is on screen where parameters are managed.
        async getScanCoverageGaps(): Promise<ScanCoverageGaps> {
            const response = await client.get('/api/parameters-discovery/scan-coverage/gaps');
            return response.data;
        },
        async getUnscannedIds(parameterIds?: number[]): Promise<{unscanned_doc_ids: string[], unscanned_param_ids: number[]}> {
            const response = await client.post('/api/parameters-discovery/scan-coverage/unscanned', {parameter_ids: parameterIds || null});
            return response.data;
        },

        // Parameter Resolution
        async resolveParameter(code: string, gnr: number, bnr: number, kommune: string, synthesize: boolean = true): Promise<ParameterResolution> {
            const params: Record<string, any> = {gnr, bnr, kommune};
            if (synthesize) params.synthesize = true;
            const response = await client.get(`/api/parameters/${code}/resolve`, {params});
            return response.data;
        },
        resolveAllParametersStream(
            request: {gnr: number; bnr: number; kommune: string; parameter_codes: string[]},
            callbacks?: {
                onParameterProgress?: (event: {parameter_code: string; status: string; current: number; total: number}) => void;
                onParameterResult?: (event: {parameter_code: string; resolution: ParameterResolution}) => void;
                onResult?: (result: {resolved_count: number; skipped_count: number; error_count: number; total: number}) => void;
                onError?: (error: string) => void;
            }
        ): {abort: () => void} {
            const abortController = new AbortController();
            authFetch('/api/parameters/resolve-all-stream', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(request),
                signal: abortController.signal,
            })
                .then(response => {
                    if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                    if (!response.body) throw new Error('No response body');
                    const reader = response.body.getReader();
                    const decoder = new TextDecoder();
                    let buffer = '';
                    let currentEventType = '';
                    const processLine = (line: string) => {
                        if (line.startsWith('event: ')) { currentEventType = line.slice(7).trim(); return; }
                        if (line.startsWith('data: ')) {
                            try {
                                const data = JSON.parse(line.slice(6));
                                switch (currentEventType) {
                                    case 'parameter_progress': callbacks?.onParameterProgress?.(data); break;
                                    case 'parameter_result': callbacks?.onParameterResult?.(data); break;
                                    case 'result': callbacks?.onResult?.(data); break;
                                    case 'error': callbacks?.onError?.(data.error); break;
                                }
                            } catch { /* ignore */ }
                            currentEventType = '';
                        }
                    };
                    const pump = (): Promise<void> => {
                        return reader.read().then(({done, value}) => {
                            if (done) return;
                            buffer += decoder.decode(value, {stream: true});
                            const lines = buffer.split('\n');
                            buffer = lines.pop() || '';
                            for (const line of lines) { if (line.trim()) processLine(line); }
                            return pump();
                        });
                    };
                    return pump();
                })
                .catch(error => {
                    if (error.name !== 'AbortError') callbacks?.onError?.(error.message || 'Resolve all stream failed');
                });
            return {abort: () => abortController.abort()};
        },
    };
}
